'use client';

import { cx } from '../../../utils/cx';

export interface SpinnerProps {
  /**
   * Sizing/colour utility classes. The spinner draws with `stroke-current`, so its
   * colour follows the surrounding text colour (set `text-*` here or on a parent).
   * @default 'size-5'
   */
  className?: string;
}

/**
 * Small circular spinner — a rotating arc over a faint track — sized for inline use
 * inside icon slots, buttons, and compact controls.
 *
 * Matches the loading indicator baked into {@link Button}: it rotates around its true
 * centre (`origin-center`) inside a square viewBox, so it stays visually symmetric in
 * an icon slot rather than wobbling like a rotated glyph icon would.
 *
 * Decorative by default (`aria-hidden`): callers convey the busy state on the
 * surrounding control (e.g. `aria-busy` on the button), so the spinner itself is not
 * announced to screen readers.
 *
 * ## Usage
 * ```tsx
 * <button aria-busy={isLoading}>
 *   {isLoading ? <Spinner className="size-5" /> : <Icon className="size-5" />}
 * </button>
 * ```
 */
export function Spinner({ className }: SpinnerProps) {
  return (
    <svg fill="none" viewBox="0 0 20 20" aria-hidden="true" className={cx('size-5', className)}>
      {/* Track */}
      <circle
        className="stroke-current opacity-30"
        cx="10"
        cy="10"
        r="8"
        fill="none"
        strokeWidth="2"
      />
      {/* Spinning arc */}
      <circle
        className="origin-center animate-spin stroke-current"
        cx="10"
        cy="10"
        r="8"
        fill="none"
        strokeWidth="2"
        strokeDasharray="12.5 50"
        strokeLinecap="round"
      />
    </svg>
  );
}

Spinner.displayName = 'Spinner';
